import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Archive,
  Trash2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import type { DeletionRequest, DeletionRequestStatus, PaginatedResponse } from '@/types';
import { ConfirmationDialog } from '@/components/users/ConfirmationDialog';
import { DeleteRequestDialog } from './DeleteRequestDialog';
import { apiClient } from '@/services/api-client';

interface DeletionRequestsTableProps {
  data: PaginatedResponse<DeletionRequest> | null;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onRefresh?: () => void;
}

const statusBadgeVariant = (status: DeletionRequestStatus) => {
  switch (status) {
    case 'pending':
      return 'warning' as const;
    case 'deleted':
      return 'destructive' as const;
    case 'cancelled':
      return 'outline' as const;
    case 'restored':
      return 'success' as const;
    default:
      return 'secondary' as const;
  }
};

function displayName(req: DeletionRequest): string {
  if (req.userFirstName && req.userLastName) {
    return `${req.userFirstName} ${req.userLastName}`;
  }
  return req.userFirstName || req.userEmail;
}

function demographicsSummary(req: DeletionRequest): string {
  const d = req.profileSnapshot?.demographics;
  const parts: string[] = [];
  if (d?.age != null) parts.push(`${d.age}y`);
  if (d?.gender) parts.push(d.gender.replace(/_/g, ' '));
  if (d?.location) parts.push(d.location);
  return parts.length ? parts.join(' · ') : '—';
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function DeletionRequestsTable({
  data,
  isLoading,
  page,
  onPageChange,
  onRefresh,
}: DeletionRequestsTableProps) {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<DeletionRequest | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notice, setNotice] = useState<{
    title: string;
    description: string;
    variant: 'success' | 'destructive';
  } | null>(null);

  const archiveMutation = useMutation({
    mutationFn: async (request: DeletionRequest) => {
      const response = await apiClient.post(
        `/api/v1/admin/deletion-requests/${request.id}/archive`
      );
      if (!response.success) {
        const error = new Error(response.error?.message ?? 'Failed to archive') as Error & {
          code?: string;
        };
        error.code = response.error?.code;
        throw error;
      }
      return response.data;
    },
    onSuccess: async (_data, request) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'deletion-requests'] });
      // Archiving disables the user, so the User Management list is stale too.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setArchiveDialogOpen(false);
      onRefresh?.();
      setNotice({
        title: 'Account archived',
        description: `${request.userEmail} has been disabled. They can no longer sign in.`,
        variant: 'success',
      });
    },
    onError: (error: Error & { code?: string }) => {
      setArchiveDialogOpen(false);
      setNotice({
        title: 'Could not archive account',
        description:
          error.code === 'FORBIDDEN'
            ? 'Only a super admin can archive another admin account.'
            : (error.message ?? 'Something went wrong.'),
        variant: 'destructive',
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (request: DeletionRequest) => {
      const response = await apiClient.post(
        `/api/v1/admin/deletion-requests/${request.id}/restore`
      );
      if (!response.success) {
        const error = new Error(response.error?.message ?? 'Failed to restore') as Error & {
          code?: string;
        };
        error.code = response.error?.code;
        throw error;
      }
      return response.data;
    },
    onSuccess: async (_data, request) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'deletion-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setRestoreDialogOpen(false);
      onRefresh?.();
      setNotice({
        title: 'Account restored',
        description: `${request.userEmail} has been re-enabled and the request closed.`,
        variant: 'success',
      });
    },
    onError: (error: Error & { code?: string }) => {
      setRestoreDialogOpen(false);
      setNotice({
        title: 'Could not restore account',
        description: error.message ?? 'Something went wrong.',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        No deletion requests found
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Demographics</TableHead>
              <TableHead>Requested</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((req) => {
              const isResolved =
                req.status === 'deleted' || req.status === 'cancelled' || req.status === 'restored';
              return (
                <TableRow key={req.id} className={isResolved ? 'opacity-70' : undefined}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-orb-gold/10 text-orb-gold text-xs">
                          {req.userFirstName?.[0] || req.userEmail[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{displayName(req)}</p>
                        <p className="text-sm text-muted-foreground">{req.userEmail}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {demographicsSummary(req)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(req.requestedAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="max-w-[16rem]">
                    <p className="truncate text-sm text-muted-foreground" title={req.reason ?? ''}>
                      {req.reason || '—'}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(req.status)}>{req.status}</Badge>
                    {req.status === 'archived' && req.reviewedAt && (
                      <span className="mt-1 block text-xs text-muted-foreground">
                        archived {daysSince(req.reviewedAt)}d ago
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {req.status === 'pending' || req.status === 'archived' ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Resolve request</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {req.status === 'pending' && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedRequest(req);
                                setArchiveDialogOpen(true);
                              }}
                            >
                              <Archive className="mr-2 h-4 w-4" />
                              Archive (disable)
                            </DropdownMenuItem>
                          )}
                          {req.status === 'archived' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setRestoreDialogOpen(true);
                                }}
                              >
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Restore (re-enable)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete permanently
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span className="sr-only">Resolved</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2 py-4">
        <p className="text-sm text-muted-foreground">
          Showing {(page - 1) * data.meta.limit + 1} to{' '}
          {Math.min(page * data.meta.limit, data.meta.total)} of {data.meta.total} requests
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {data.meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= data.meta.totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Archive confirmation — the deliberate first step before any deletion */}
      <ConfirmationDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive account"
        description={`This disables ${selectedRequest?.userEmail ?? 'this user'} (reversible) — they can no longer sign in, but all data is kept. You can permanently delete or restore the account later. Continue?`}
        confirmText="Archive"
        variant="default"
        isLoading={archiveMutation.isPending}
        onConfirm={() => selectedRequest && archiveMutation.mutate(selectedRequest)}
      />

      {/* Restore confirmation — re-enable an archived account */}
      <ConfirmationDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        title="Restore account"
        description={`Re-enable ${selectedRequest?.userEmail ?? 'this user'} and close their deletion request? They will be able to sign in again.`}
        confirmText="Restore"
        variant="default"
        isLoading={restoreMutation.isPending}
        onConfirm={() => selectedRequest && restoreMutation.mutate(selectedRequest)}
      />

      {/* Delete (hard) confirmation */}
      <DeleteRequestDialog
        request={selectedRequest}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={() => {
          onRefresh?.();
          setNotice({
            title: 'Account deleted',
            description: 'The account and all associated data were permanently removed.',
            variant: 'destructive',
          });
        }}
      />

      {notice && (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-50 max-w-md rounded-lg border bg-background p-4 shadow-lg ${
            notice.variant === 'destructive' ? 'border-destructive/40' : 'border-orb-teal/40'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p
                className={`font-medium ${
                  notice.variant === 'destructive' ? 'text-destructive' : 'text-orb-teal'
                }`}
              >
                {notice.title}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{notice.description}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setNotice(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
